import {TZDate} from "react-day-picker";
import * as Yup from "yup";

import {shortDateTimeUser} from "@/lib/format.tsx";
import {AccountUser} from "@/lib/types/accounts.ts";
import {
  AffectedComponent,
  ComponentStatusValue,
  Incident,
  IncidentState,
  IncidentType,
  IncidentUpdate,
} from "@/lib/types/statuspages.ts";
import {
  AffectedComponent as FormAffectedComponent,
  IncidentEditFormData,
  Update,
} from "@/pages/incidents/components/incident-edit/IncidentForm.types.ts";

// a component left at "No Change" is not attached to the incident, which also leaves its status alone
export const isAttachedComponent = (component: FormAffectedComponent) =>
  !!component.id && component.status !== ComponentStatusValue.NO_CHANGE;

// the newest update sits at index 0, so it carries the state the incident is being saved with
export const isNotificationIncident = (updates?: Update[]) =>
  updates?.[0]?.incident_state === IncidentState.NOTIFICATION;

export const updateIncidentFormDefaultValuesBuilder = ({
  incident,
  timezone,
  defaultIncidentType,
  statusPageId,
  user,
}: {
  incident?: Incident;
  timezone: string;
  defaultIncidentType: IncidentType;
  statusPageId?: number;
  user: AccountUser;
}): IncidentEditFormData => {
  const incidentType = incident?.incident_type || defaultIncidentType || IncidentType.INCIDENT;
  const isScheduledMaintenance = incidentType === IncidentType.SCHEDULED_MAINTENANCE;

  const updates: Partial<Update>[] = incident?.updates.map((iu: IncidentUpdate) => ({
    id: iu.id,
    description: iu.description,
    description_html: iu.description_html,
    incident_state: iu.incident_state,
    updatedAt: iu.updated_at && user && shortDateTimeUser(iu.updated_at, user),
    confirmed: true, // existing updates are already confirmed
  })) || [
    {
      description: "",
      incident_state: isScheduledMaintenance ? IncidentState.MAINTENANCE : undefined,
      confirmed: false, // new update needs to be confirmed
    },
  ];

  const affectedComponents: AffectedComponent[] =
    incident?.affected_components.map((c: AffectedComponent) => ({
      ...c,
    })) || [];

  return {
    id: incident?.id,
    name: incident?.name || "",
    starts_at: incident?.starts_at ? new TZDate(incident?.starts_at, timezone) : new TZDate(new Date(), timezone),
    ends_at: incident?.ends_at ? new TZDate(incident?.ends_at, timezone) : undefined,
    statuspage_id: incident?.statuspage.id || statusPageId,
    incident_type: incidentType,
    include_in_global_metrics: incident?.include_in_global_metrics ?? true,
    resolve_components: false,
    updates: updates as Update[],
    notify_subscribers: incident?.notify_subscribers ?? false,
    affected_components: affectedComponents,
    send_maintenance_start_notification: incident?.send_maintenance_start_notification || false,
  };
};

export const updateIncidentFormSchemaBuilder = ({
  isScheduledMaintenance,
  timezone,
}: {
  isScheduledMaintenance: boolean;
  timezone: string;
}) => {
  const currentDate = new TZDate(new Date(), timezone);

  const startsAtSchema = isScheduledMaintenance
    ? Yup.date().required("Maintenance start time is required.")
    : Yup.date()
        .required("Incident start time is required.")
        .max(currentDate, "Incident start date can not be in future.");

  let endsAtSchema = Yup.date()
    .min(Yup.ref("starts_at"), "Incident end time can not be before the start time.")
    .nullable();
  if (isScheduledMaintenance) {
    endsAtSchema = endsAtSchema.required("Maintenance end time is required.");
  }

  return Yup.object({
    id: Yup.number(),
    statuspage_id: Yup.number().required("This field is required."),
    incident_type: Yup.mixed<IncidentType>().oneOf(Object.values(IncidentType)).defined(),
    name: Yup.string().required("This field is required.").max(255, "Title must be at most 255 characters."),
    include_in_global_metrics: Yup.boolean().default(true),
    starts_at: startsAtSchema,
    ends_at: endsAtSchema,
    resolve_components: Yup.boolean().default(false),
    affected_components: Yup.array().of(
      Yup.object({
        id: Yup.number().defined(),
        obj_id: Yup.number().optional(),
        status: Yup.string().defined(),
      })
    ),
    updates: Yup.array()
      .of(
        Yup.object({
          id: Yup.number().optional(),
          incident_state: Yup.mixed<IncidentState>()
            .oneOf(Object.values(IncidentState))
            .required("Please select Incident State."),
          description: Yup.string().optional(),
          description_html: Yup.string().optional(),
          confirmed: Yup.boolean().when("id", {
            is: (id: number | undefined) => !id,
            then: (schema) => schema.oneOf([true], "Please click Add to confirm the update before submitting."),
            otherwise: (schema) => schema.optional(),
          }),
        })
      )
      .required(),
    notify_subscribers: Yup.boolean().default(false),
    send_maintenance_start_notification: Yup.boolean().default(false),
  });
};
